import {
	BaseUnsavedChangesPrompt,
	Drawer,
	DrawerHeader,
	DrawerHeading,
	DrawerProps,
	Id,
	Loading,
	useDrawerUnsavedChangesPrompt,
} from "@dgs/core";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { contactService } from "../../shared/api/contactService";
import { dataFieldService } from "../../shared/api/dataFieldService";
import { useContactView } from "../../shared/hooks/useContactView";
import {
	ContactCreateAdditionalEmailRequest,
	ContactCreateAdditionalPhoneNumberRequest,
	ContactCreateAddressRequest,
	ContactCreateCompanyRequest,
	ContactCreateDetailsRequest,
	ContactCreateMasterDataRequest,
	ContactCreateRequest,
} from "../../shared/types";
import { useGlobalTenants } from "../../tenants/global/GlobalTenantProvider";
import { AdditionalForm, AdditionalFormValues, emptyAdditionalFormValues } from "./AdditionalForm";
import { AddressesForm, AddressesFormValues, emptyAddressesFormValues } from "./AddressesForm";
import { CompaniesForm, CompaniesFormValues, emptyCompaniesFormValues } from "./CompaniesForm";
import { CreateContactStepKey, CreateContactStepper, createContactSteps } from "./CreateContactStepper";
import { DetailsForm, DetailsFormValues, emptyDetailsFormValues } from "./DetailsForm";
import { MasterDataForm, MasterDataFormValues, emptyMasterDataFormValues } from "./MasterDataForm";

interface CreateContactFormValues {
	masterData: MasterDataFormValues;
	details: DetailsFormValues;
	companies: CompaniesFormValues;
	addresses: AddressesFormValues;
	additional: AdditionalFormValues;
}

const emptyCreateContactFormValues: CreateContactFormValues = {
	masterData: emptyMasterDataFormValues,
	details: emptyDetailsFormValues,
	companies: emptyCompaniesFormValues,
	addresses: emptyAddressesFormValues,
	additional: emptyAdditionalFormValues,
};

interface CreateContactDrawerContentProps {
	currentContactViewId: Id;
	onSubmit: (values: ContactCreateRequest) => Promise<void>;
	setIsDirty: (dirty: boolean) => void;
}

const isStepDone = (stepKey: CreateContactStepKey, activeStepKey: CreateContactStepKey) =>
	createContactSteps.findIndex((s) => s.key === activeStepKey) >=
	createContactSteps.findIndex((s) => s.key === stepKey);

const mapFormValuesToRequest = (
	submittingStep: Exclude<CreateContactStepKey, "masterData" | "details">,
	values: CreateContactFormValues,
) => {
	const masterDataRequest: ContactCreateMasterDataRequest = {
		contactTypeId: values.masterData.contactType?.id as Id,
		firstName: values.masterData.firstName,
		lastName: values.masterData.lastName,
		primaryEmail: values.masterData.primaryEmail,
		primaryEmailType: values.masterData.primaryEmailType,
		primaryPhoneNumber: values.masterData.primaryPhoneNumber,
		primaryPhoneNumberType: values.masterData.primaryPhoneNumberType,
		tagIds: values.masterData.tags.map(({ id }) => id),
		tenantIds: values.masterData.tenants.map(({ id }) => id),
		locale: values.masterData.locale,
	};

	const detailsRequest: ContactCreateDetailsRequest[] = Object.entries(values.details).map(([dataFieldId, value]) => ({
		dataFieldId,
		value,
	}));

	let companiesRequest: ContactCreateCompanyRequest[] = [];

	if (isStepDone("companies", submittingStep)) {
		companiesRequest = values.companies.companies.map((companyValues) => ({
			companyId: companyValues.company?.id as Id,
			position: companyValues.position,
			isPrimary: companyValues.isPrimary,
			isEvent: companyValues.isEvent,
			isCarFleet: companyValues.isCarFleet,
		}));
	}

	let addressesRequest: ContactCreateAddressRequest[] = [];
	if (isStepDone("addresses", submittingStep)) {
		addressesRequest = values.addresses.addresses.map((addressValues) => ({
			city: addressValues.city,
			type: addressValues.type,
			countryId: addressValues.country?.id as Id,
			zip: addressValues.zip,
			street: addressValues.street,
			additionalInfo: addressValues.additionalInfo,
			houseNumber: addressValues.houseNumber,
			isPrimary: addressValues.isPrimary,
		}));
	}

	let additionalEmailAddresses: ContactCreateAdditionalEmailRequest[] = [];
	let additionalPhoneNumbers: ContactCreateAdditionalPhoneNumberRequest[] = [];
	if (isStepDone("additional", submittingStep)) {
		additionalEmailAddresses = values.additional.emails;
		additionalPhoneNumbers = values.additional.phoneNumbers;
	}

	const request: ContactCreateRequest = {
		masterData: masterDataRequest,
		details: detailsRequest,
		addresses: addressesRequest,
		companies: companiesRequest,
		additionalEmails: additionalEmailAddresses,
		additionalPhoneNumbers: additionalPhoneNumbers,
	};

	return request;
};

const CreateContactDrawerContent: FC<CreateContactDrawerContentProps> = ({
	currentContactViewId,
	onSubmit,
	setIsDirty,
}) => {
	const { selected } = useGlobalTenants();
	const { i18n } = useTranslation();
	const { data: dataFields } = useQuery({
		queryKey: [...dataFieldService.indexByContactViewKey, currentContactViewId],
		queryFn: () =>
			dataFieldService
				.indexByContactView({ params: { contactViewId: currentContactViewId } })
				.then((response) => response.data.data),
	});

	const [[step, formValues], setFormValues] = useState<[CreateContactStepKey, CreateContactFormValues]>([
		"masterData",
		{
			...emptyCreateContactFormValues,
			masterData: {
				...emptyCreateContactFormValues.masterData,
				tenants: selected.tenant ? [selected.tenant] : [],
				locale: emptyCreateContactFormValues.masterData.locale || i18n.language,
			},
		},
	]);

	const handleGoBack = (stepKey: Exclude<CreateContactStepKey, "masterData">) => {
		const index = dataFields?.length === 0 && stepKey === "companies" ? 2 : 1;
		const step = createContactSteps[createContactSteps.findIndex((s) => s.key === stepKey) - index].key;
		setFormValues(([, values]) => [step, values]);
	};

	const handleSubmit = (step: CreateContactStepKey, formValues: CreateContactFormValues) => {
		setIsDirty(true);
		setFormValues([step, formValues]);
	};

	const handleSave = async (
		submittingStep: Exclude<CreateContactStepKey, "masterData" | "details">,
		values: CreateContactFormValues,
	) => {
		const request = mapFormValuesToRequest(submittingStep, values);

		await onSubmit(request);
	};

	if (dataFields === undefined) {
		return <Loading />;
	}

	const excludeDetailsStep = dataFields.length === 0;

	switch (step) {
		case "masterData":
			return (
				<>
					<CreateContactStepper activeStep={step} excludeDetailsStep={excludeDetailsStep} />
					<MasterDataForm
						setIsDirty={setIsDirty}
						initialValues={formValues.masterData}
						onSubmit={(values) =>
							handleSubmit(excludeDetailsStep ? "companies" : "details", { ...formValues, masterData: values })
						}
					/>
				</>
			);
		case "details":
			return (
				<>
					<CreateContactStepper activeStep={step} excludeDetailsStep={excludeDetailsStep} />
					<DetailsForm
						dataFields={dataFields}
						initialValues={formValues.details}
						onGoBack={() => handleGoBack("details")}
						onSubmit={(values) => handleSubmit("companies", { ...formValues, details: values })}
					/>
				</>
			);
		case "companies":
			return (
				<>
					<CreateContactStepper activeStep={step} excludeDetailsStep={excludeDetailsStep} />
					<CompaniesForm
						initialValues={formValues.companies}
						onGoBack={() => handleGoBack("companies")}
						onSubmit={async (values) => {
							const nextValues: CreateContactFormValues = { ...formValues, companies: values };
							switch (values.action) {
								case "save":
									await handleSave("companies", nextValues);
									break;
								case "next":
									handleSubmit("addresses", nextValues);
									break;
							}
						}}
					/>
				</>
			);
		case "addresses":
			return (
				<>
					<CreateContactStepper activeStep={step} excludeDetailsStep={excludeDetailsStep} />
					<AddressesForm
						initialValues={formValues.addresses}
						onGoBack={() => handleGoBack("addresses")}
						onSubmit={async (values) => {
							const nextValues: CreateContactFormValues = { ...formValues, addresses: values };
							switch (values.action) {
								case "save":
									await handleSave("addresses", nextValues);
									break;
								case "next":
									handleSubmit("additional", nextValues);
									break;
							}
						}}
					/>
				</>
			);
		case "additional":
			return (
				<>
					<CreateContactStepper activeStep={step} excludeDetailsStep={excludeDetailsStep} />
					<AdditionalForm
						initialValues={formValues.additional}
						onGoBack={() => handleGoBack("additional")}
						onSubmit={(values) => handleSave("additional", { ...formValues, additional: values })}
					/>
				</>
			);
	}
};

const WideDrawer = styled(Drawer)`
	width: 800px;
`;

interface Props extends DrawerProps {
	onSubmit: (values: ContactCreateRequest) => Promise<boolean>;
	onClose: () => void;
}

export const CreateContactDrawer: FC<Props> = ({ onSubmit, onClose, ...drawerProps }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const { currentContactView } = useContactView();
	const { handleClose, setIsDirty, ...promptProps } = useDrawerUnsavedChangesPrompt(onClose);

	const handleSubmit = async (request: ContactCreateRequest) => {
		if (await onSubmit(request)) {
			onClose();
		}
	};

	const handleClosed = () => {
		void queryClient.invalidateQueries({
			queryKey: [...contactService.keys.baseKey, "check-email"],
			refetchType: "none",
		});
	};

	return (
		<>
			<BaseUnsavedChangesPrompt {...promptProps} />
			<WideDrawer {...drawerProps} onClose={handleClose} onClosed={handleClosed}>
				<DrawerHeader onClose={handleClose}>
					<DrawerHeading>{t("Add contact")}</DrawerHeading>
				</DrawerHeader>
				{currentContactView ? (
					<CreateContactDrawerContent
						onSubmit={handleSubmit}
						setIsDirty={setIsDirty}
						currentContactViewId={currentContactView.id}
					/>
				) : (
					<Loading />
				)}
			</WideDrawer>
		</>
	);
};
